/**
 * Common translations: common, help, keyboard, password, notifications, breadcrumbs, quickActions, globalSearch, ticket, validation, pageTitles, errors
 */

export const common = {
  notFoundPage: {
    title: 'Page not found',
    description: 'This page does not exist, or its address has changed.',
    backHome: 'Back to the homepage'
  },
  // BCP-47 locale tag for toLocaleDateString and similar APIs.
  locale: 'en-GB',
  save: 'Save',
  cancel: 'Cancel',
  delete: 'Delete',
  confirm: 'Confirm',
  done: 'Done',
  edit: 'Edit',
  add: 'Add',
  back: 'Back',
  next: 'Next',
  loading: 'Loading...',
  error: 'Error',
  success: 'Success',
  important: 'Important',
  close: 'Close',
  search: 'Search',
  filter: 'Filter',
  reset: 'Reset',
  retry: 'Retry',
  freeForever: 'Free forever',
  upgradeAnytime: 'Upgrade to a paid tier anytime.',
  yes: 'Yes',
  no: 'No',
  or: 'or',
  all: 'All',
  none: 'None',
  optional: 'Optional',
  required: 'Required',
  active: 'Active',
  creating: 'Creating...',
  settings: 'Settings',
  copied: 'Copied',
  refresh: 'Refresh',
  justNow: 'just now',
  minutesAgo: '{n}m ago',
  hoursAgo: '{n}h ago',
  daysAgo: '{n}d ago',
  viewAll: 'View all',
  termPrice: {
    period: {
      month: 'month',
      quarter: 'quarter',
      halfYear: 'half-year',
      year: 'year'
    },
    monthlyEquivalent: 'equals {price} / month'
  },
  // The billing row of a plan and the dialog that changes its period for the
  // next renewal.
  billingPeriod: {
    label: 'Billing period',
    periods: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      halfAnnually: 'Half-yearly',
      annually: 'Annually'
    },
    renewsOn: 'Renews on {date}',
    hourly: 'Billed hourly, at most the monthly price per calendar month',
    scheduled: 'From {date}: {period}',
    change: 'Change period',
    withdraw: 'Withdraw change',
    withdrawn: 'The change of the billing period has been withdrawn.',
    dialog: {
      title: 'Change billing period',
      current: 'Current period: {period}',
      effective: 'The change takes effect at the end of your current billing period on {date}. From then on the new period applies, and each period is followed by one of the same length. Until then you can withdraw the change here.',
      price: '{price} per {period}',
      hourlyPrice: 'billed hourly, at most {price} per month',
      from: 'from {date}',
      scheduled: 'Already scheduled: {period} from {date}.',
      none: 'This contract offers no other billing period.',
      confirm: 'Change period',
      cancel: 'Cancel',
      success: 'The change of the billing period is scheduled.'
    }
  },
  paymentErrors: {
    declined: 'The payment could not be collected. Nothing was charged. Please check your payment method or add a different one.',
    authenticationRequired: 'Your bank requires a confirmation for this payment that cannot be given without you. Nothing was charged. Please add a different payment method.'
  },
  termSwitch: {
    keepsTerm: 'Your paid period stays and runs until {date}. From the next period your plan costs {newPrice} per {period}.',
    charge: 'For the time from today until {date} we now charge the difference of {amount} to your saved payment method. The switch is made once the payment has arrived.',
    refund: 'For the time from today until {date} we refund the difference of {amount} within 14 days after the switch to the payment method originally used.',
    refundSepa: 'If you paid by SEPA direct debit, we refund that part within 14 days after the eight-week period in which your bank can still return the debit has expired.',
    leaveRefund: 'Your current plan ends with the switch. We refund the paid time after it pro rata by the day, {amount}, within 14 days after the switch to the payment method originally used. Pay-as-you-go is billed monthly by usage.',
    nothingDue: 'Nothing is due now for the current period.',
    // Monthly switches. A Kubernetes block is these sentences in order:
    // charge, refund when the old period was paid, usageBefore when leaving
    // pay-as-you-go, next, openEnded.
    monthly: {
      k8sCharge: 'You are switching the namespace {namespace} from {oldPlan} to {newPlan}. The switch takes effect at once. For the rest of the current month up to {monthEnd} we charge you {amount} {vat} now for {newPlan}.',
      k8sChargeBase: 'You are switching the namespace {namespace} from {oldPlan} to {newPlan}. The switch takes effect at once. For the rest of the current month up to {monthEnd} we charge you {amount} {vat} now as the base fee for {newPlan}.',
      k8sRefund: 'The fee of {refund} already paid for {oldPlan} for the time from the switch to {oldPaidEnd} is refunded within 14 days of the switch to the payment method you used originally.',
      k8sRefundBase: 'The base fee of {refund} already paid for {oldPlan} for the time from the switch to {oldPaidEnd} is refunded within 14 days of the switch to the payment method you used originally.',
      k8sUsageBefore: 'Your usage up to the switch is billed with the next invoice at the previous usage prices.',
      k8sNext: 'From {nextFirst} {newPlan} costs {newPrice} per month {vat}.',
      k8sNextBase: 'From {nextFirst} {newPlan} costs {basePrice} base fee per month {vat}. Your usage from the switch is billed monthly by consumption: {usagePrices} {vat}.',
      openEnded: 'The contract continues without a fixed term, and you can cancel it at any time with one month\'s notice.',
      hosting: 'You are switching your web hosting package {identifier} from {oldPlan} to {newPlan}. The switch takes effect at once. From the next billing month {newPlan} costs {newPrice} per month {vat}. For the rest of the current month up to {periodEnd} we charge you the difference of {amount} {vat} with your next invoice; nothing is debited today. The contract continues without a fixed term, and you can cancel it at any time with one month\'s notice.'
    },
    freeToPaid: {
      intro: 'You are switching the namespace {namespace} from the free tier to {newPlan}.',
      prices: 'Prices {vat}: {usagePrices}{basePriceSentence}. We bill monthly by actual use; there is no minimum term. You can cancel the contract at any time with one month\'s notice.',
      basePriceSentence: ', plus {basePrice} per month',
      chargeNow: 'For the rest of the current month up to {monthEnd} we charge you {amount} {vat} now as the base fee for {newPlan} to your saved payment method; from {nextFirst} {newPlan} costs {basePrice} base fee per month {vat}.'
    },
    usagePrices: '{cpuPrice} per {cpuUnit}, {ramPrice} per {ramUnit}, {storagePrice} per GB of storage and month, {egressPrice} per GB of outbound traffic',
    units: {
      cpu: 'core-hour',
      ram: 'GiB-hour'
    },
    vat: {
      gross: '(incl. VAT)',
      net: '(excl. VAT)'
    },
    switchWithdrawalNote: 'As a consumer you can withdraw from this switch within 14 days. Your previous plan then returns, and your existing contract is unaffected. Because you ask us to carry out the switch at once, on a withdrawal you pay the new plan\'s price pro rata for the time until then; what you already paid for the old plan for that time is credited. We refund the rest of the amount charged today within 14 days. {link}',
    switchWithdrawalNoteNextInvoice: 'As a consumer you can withdraw from this switch within 14 days. Your previous plan then returns, and your existing contract is unaffected. The price difference for the switch comes with your next invoice. Because you ask us to carry out the switch at once, on a withdrawal we charge only the share of it for the time until then. {link}',
    switchWithdrawalNoteFreeToPaid: 'As a consumer you can withdraw from this switch within 14 days; your namespace then returns to the free tier. Because you ask us to carry out the switch at once, on a withdrawal you pay the pro rata base fee and your usage for the time until then at the prices shown above; the base fee charged today is credited against it and the rest refunded within 14 days. {link}',
    switchWithdrawalLink: 'Withdrawal instructions',
    switchWithdrawalConsent: 'I expressly request that you carry out the switch before the withdrawal period expires and begin the service in the new tier. I am aware that my right of withdrawal expires once you have performed the service in full.',
    confirmPaid: 'Switch and pay',
    // The line for an owner on the current terms, the box
    // for one with terms to accept, its document list cut to what the terms
    // version covers. The comparison sentence is left out where no comparison
    // can be linked.
    terms: {
      line: 'Our {agb} and service agreements in the version of {version} apply to this switch.',
      holder: 'I accept the new version of our contract terms of {version} ({agb} and service agreements) for all contracts of my account.',
      contract: 'I accept the new version of our contract terms of {version} ({agb} and service agreements) for this contract.',
      compare: 'The {link} shows what has changed.',
      agbLink: 'terms',
      compareLink: 'comparison'
    },
    pendingTitle: 'Switch started',
    pending: 'Your SEPA direct debit is still being collected. The switch is made once it has arrived. If the debit fails, your current tier stays and nothing is charged.',
    resources: {
      cpu: 'CPU',
      memory_gib: 'memory',
      pvcs: 'volumes',
      storage_gib: 'storage',
      other: 'resources'
    },
    errors: {
      noPaymentMethod: 'The switch needs a saved payment method. Nothing was charged and nothing was switched.',
      paymentFailed: 'The payment was declined. Nothing was charged and nothing was switched. Please check your payment method.',
      authenticationRequired: 'Your bank requires a confirmation for this payment that cannot be given without you. Nothing was charged and nothing was switched. Please add a different payment method.',
      quoteStale: 'The amount has changed in the meantime. Please check the new amount and confirm again.',
      quotaExceeded: 'Your namespace uses more {resource} than the new plan allows: {used} against {limit}. Please reduce the usage and try again.',
      inProgress: 'A switch of this plan is already in progress. Please wait until it has finished.',
      cancellationPending: 'This plan is cancelled. Undo the cancellation to switch it.',
      paymentOverdue: 'A payment for this plan is open. Please settle it before you switch.',
      wertersatzUnpaid: 'Compensation from an earlier withdrawal is still unpaid. Please pay its invoice before you switch.',
      withdrawalSettling: 'An earlier withdrawal is not settled yet because payments for it are still being processed.',
      withdrawn: 'A withdrawal from this contract has already reached us.',
      planChanged: 'Your plan has just changed. Please reload the page and try again.',
      unavailable: 'The switch is not possible right now. Please try again later or contact support.',
      unconfirmed: 'We could not confirm the payment yet. Please do not try again: if the payment is confirmed we complete the switch automatically; if it is not, we refund any amount already debited.',
      termsRequired: 'For this switch we need your acceptance of the current version of our contract terms. Please tick the box and confirm again. Nothing was charged and nothing was switched.',
      termsAcceptanceRequired: 'For this switch we first need your acceptance of the current version of our contract terms. Acceptance is only possible in the customer portal: sign in there, accept, and start the switch from there. Nothing was charged and nothing was switched.',
      consentRequired: 'For this switch we need your consent that we carry it out before the withdrawal period ends. Please tick the box and confirm again. Nothing was charged and nothing was switched.',
      notOnStripe: 'This contract is not billed through our payment system yet, so you cannot change its billing period yourself. Please contact support.',
      switchWindowOpen: 'You can still withdraw from your last tier switch. You can change the billing period once the withdrawal period has expired.',
      switchPaymentDue: 'The difference for a tier switch of your vServer is still unpaid. Pay it or switch the server back to its previous tier; until then no other change is possible.',
      resizeInProgress: 'Your vServer is being resized right now. Please wait until the resize has finished.',
      periodNotStarted: 'You can change the billing period once your vServer has run for the first time.',
      periodChangeScheduled: 'A change of the billing period is scheduled for this contract. Until it takes effect, neither a tier switch nor another change of the period is possible. You can withdraw it under “Billing period”.',
      periodUnavailable: 'This billing period is not available for your contract.',
      mailTakeoverPending: 'A hosting package is taking over email for this domain; the plan ends on its own with a refund.',
      samePeriod: 'Your contract already runs on this billing period.',
      noChangeScheduled: 'No change of the billing period is scheduled for this contract.',
      changeInEffect: 'The change of the billing period has already taken effect and can no longer be withdrawn.',
      subscriptionBusy: 'Another change to this contract is being processed right now. Please try again in a minute.',
      stripeUnavailable: 'Our payment provider cannot be reached right now. Nothing was changed. Please try again later.'
    }
  }
} as const

export const help = {
  title: 'Help & Contact',
  weHelpYou: 'We are happy to help.'
} as const

export const keyboard = {
  title: 'Keyboard Shortcuts',
  categories: {
    search: 'Search',
    navigation: 'Navigation',
    general: 'General',
    dev: 'Development'
  },
  noShortcuts: 'No shortcuts in this category',
  ctrlKNote: 'Ctrl+K also opens the search'
} as const

export const password = {
  strength: {
    veryWeak: 'very weak',
    weak: 'weak',
    fair: 'fair',
    good: 'good',
    strong: 'strong',
    label: '{strength} password'
  },
  requirements: {
    title: 'Password Requirements',
    minLength: 'At least 8 characters',
    uppercase: 'Uppercase letter (A-Z)',
    lowercase: 'Lowercase letter (a-z)',
    number: 'Number (0-9)'
  }
} as const

export const notifications = {
  title: 'Notifications',
  ariaLabel: 'Notifications',
  markAllRead: 'Mark all as read',
  empty: 'No notifications',
  categories: {
    renewal: 'Renewal',
    security: 'Security',
    billing: 'Billing',
    system: 'System',
    verification: 'Verification'
  },
  saved: 'Saved successfully',
  updated: 'Updated successfully',
  deleted: 'Deleted successfully',
  created: 'Created successfully',
  addedToCart: 'Added to cart',
  copiedToClipboard: 'Copied to clipboard'
} as const

export const breadcrumbs = {
  dashboard: 'Dashboard',
  admin: 'Admin',
  email: 'Email',
  domains: 'Domains',
  packet: 'Packages',
  invoice: 'Invoices',
  account: 'Account',
  product: 'Products',
  user: 'Users',
  details: 'Details',
  security: 'Security'
} as const

export const quickActions = {
  title: 'Quick Actions',
  ariaLabel: 'Quick Actions',
  newTicket: 'New Ticket',
  orderProduct: 'Order Product',
  domains: 'Domains',
  newProduct: 'New Product',
  adminBadge: 'Admin'
} as const

export const globalSearch = {
  triggerText: 'Search...',
  openSearch: 'Open search',
  openSearchShortcut: 'Open search (Ctrl+K)',
  placeholder: 'Search for pages, products, domains...',
  clearInput: 'Clear input',
  closeSearch: 'Close search',
  noResults: 'No results for "{query}"',
  recentSearches: 'Recent Searches',
  clearRecent: 'Clear',
  hints: {
    navigate: 'navigate',
    select: 'select',
    close: 'close'
  },
  groups: {
    domain: 'Domain',
    navigation: 'Navigation',
    product: 'Products'
  },
  domainResult: {
    title: 'Check domain "{query}"',
    subtitle: 'Check availability'
  },
  nav: {
    home: 'Home',
    kubernetes: 'Kubernetes',
    kubernetesSub: 'Container orchestration',
    vserver: 'VServer',
    vserverSub: 'Virtual private servers',
    webhosting: 'Web Hosting',
    webhostingSub: 'Hosting packages',
    domains: 'Domains',
    domainsSub: 'Search & register a domain',
    products: 'All Products',
    productsSub: 'Product overview',
    cart: 'Shopping cart',
    login: 'Sign in',
    register: 'Register',
    dashboard: 'Dashboard',
    dashboardSub: 'My area',
    impress: 'Legal Notice',
    privacy: 'Privacy Policy',
    agb: 'Terms & Conditions',
    agbSub: 'General Terms and Conditions'
  }
} as const

export const ticket = {
  title: 'Support Ticket',
  subtitle: 'How can we help you?',
  responseTime: 'Our support team typically responds within 24 hours.',
  loadError: 'The ticket form could not be loaded. Please try again later or email us directly at support{\'@\'}itsh.dev.'
} as const

export const a11y = {
  skipToContent: 'Skip to content',
  mainNavigation: 'Main navigation',
  bottomNavigation: 'Bottom navigation',
  shoppingCart: 'Shopping cart',
  goToCart: 'Go to cart',
  logoToHome: 'ITSH - To homepage'
} as const

export const validation = {
  required: 'Required',
  email: 'Invalid email address',
  emailFormat: 'Invalid email format',
  number: 'Only numbers allowed',
  minLength: 'At least {min} characters required',
  maxLength: 'Maximum {max} characters allowed',
  phone: 'Only digits allowed (no spaces)',
  postcode: 'Postcode must have 5 digits',
  alphanumeric: 'Only letters, numbers and punctuation allowed',
  noHtml: 'HTML tags are not allowed',
  invalidInput: 'Invalid input',
  invalidPostcodeCountry: "The postal code doesn't match the format of the selected country. Please check your input.",
  postcodeStateMismatch: "The postal code isn't located in the selected state. Please check your input.",
  ipv4: 'Invalid IPv4 address',
  ipv6: 'Invalid IPv6 address',
  hostname: 'Invalid hostname',
  port: 'Must be between 0 and 65535',
  numeric: 'Must be a number',
  emailMissingAt: "Email must contain an {'@'}",
  emailMissingDomain: "Please enter a domain after {'@'} (e.g. example.com)",
  emailInvalidDomain: 'Domain must contain a dot (e.g. example.com)',
  passwordTooShort: 'At least {min} characters required (currently: {current})',
  passwordNeedsUppercase: 'At least one uppercase letter (A-Z) required',
  passwordNeedsLowercase: 'At least one lowercase letter (a-z) required',
  passwordNeedsNumber: 'At least one number (0-9) required',
  phoneNoLetters: 'Phone number must not contain letters',
  phoneNoSpaces: 'Please enter phone number without spaces',
  phoneTooShort: 'Phone number is too short (minimum {min} digits)',
  phoneTooLong: 'Phone number is too long (maximum {max} digits)',
  postcodeTooShort: 'Postcode must have 5 digits (currently: {current})',
  postcodeNoLetters: 'Postcode must only contain digits',
  nameNoNumbers: 'Name must not contain numbers',
  nameInvalidChars: 'Name contains invalid characters'
} as const

export const pageTitles = {
  // Bare page names. The 'ITSH | ' prefix is composed in the router's
  // afterEach hook: vue-i18n reads `|` as the plural delimiter, so storing
  // the full title here truncates it to 'ITSH' at render time.
  products: 'Products',
  kubernetes: 'Managed Kubernetes',
  objectStorage: 'Object Storage',
  webhosting: 'Web Hosting',
  domainSearch: 'Register Domain',
  tldCatalog: 'TLD Overview',
  impress: 'Legal Notice',
  privacy: 'Privacy Policy',
  agb: 'Terms & Conditions',
  sla: 'SLA',
  cancellation: 'Terminate contracts',
  cancellationConfirm: 'Confirm cancellation',
  withdrawal: 'Withdrawal instructions',
  nis2: 'NIS2',
  pricingCalculator: 'Pricing Calculator',
  vserverCompare: 'VServer comparison'
} as const

export const errors = {
  generic: 'An error occurred. Please try again later.',
  serviceUnavailable: 'The service is currently unavailable. Please try again later.',
  gatewayError: 'The server is temporarily unreachable. Please try again later.',
  networkError: 'Network error. Please check your internet connection.',
  unauthorized: 'Unauthorised. Please sign in again.',
  forbidden: 'Access denied.',
  notFound: 'The requested resource was not found.',
  conflict: 'Conflict. The resource already exists.',
  tooManyRequests: 'Too many requests. Please wait a moment.',
  saveFailed: 'Failed to save',
  loadFailed: 'Failed to load',
  deleteFailed: 'Failed to delete'
} as const
